import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, CircularProgress,
  Typography, Link,
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import * as codeApi from '../../lib/api/commonCode';
import { HEADER_LINKS_CHANGED } from '../../lib/headerLinks';

// 헤더 오른쪽 위 "업무용 사이트" 드롭다운이 읽는 자리다. 코드값 칸(BLC/MLC/SLC)은 전부 고정이고
// 실제 내용은 REV_VAL_1(이름)과 REV_VAL_2(주소)에 들어간다 - 그래서 일반 공통코드 탭으로는
// 등록하기가 까다로웠다. 이 탭은 그 고정값을 화면이 대신 채워 준다.
const LINK_GROUP = {
  blcCd: 'BASE',
  mlcCd: 'HEADER',
  slcCd: 'LINKS',
  itemNm: '업무용사이트',
};

const EMPTY_FORM = { name: '', url: '', step: '1', useYn: 'Y' };

const isValidUrl = (value) => /^https?:\/\/\S+$/i.test(value.trim());

const LinkCodeTabPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await codeApi.getComCodes({
        blcCd: LINK_GROUP.blcCd,
        mlcCd: LINK_GROUP.mlcCd,
        slcCd: LINK_GROUP.slcCd,
      });
      const list = res.data?.list || [];
      const ordered = [...list].sort(
        (a, b) => Number(a.step || 0) - Number(b.step || 0),
      );
      setRows(ordered.map((r, i) => ({ id: r.commonUuid || i, ...r })));
    } catch {
      enqueueSnackbar('바로가기 목록을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const nextStep = () =>
    String(rows.reduce((max, r) => Math.max(max, Number(r.step || 0)), 0) + 1);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, step: nextStep() });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.revVal1 || '',
      url: row.revVal2 || '',
      step: row.step || '1',
      useYn: row.useYn || 'Y',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const url = form.url.trim();

    if (!name) {
      enqueueSnackbar('이름을 입력해 주세요.', { variant: 'warning' });
      return;
    }
    if (!isValidUrl(url)) {
      enqueueSnackbar('주소는 http:// 또는 https:// 로 시작해야 합니다.', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // 수정 쿼리가 조건 없이 전 컬럼을 덮어쓴다. 이 화면이 안 쓰는 칸도 원래 값을 그대로
      // 실어 보내지 않으면 저장할 때마다 NULL 로 날아간다.
      const payload = {
        ...LINK_GROUP,
        itemDesc: editing?.itemDesc || '',
        revVal1: name,
        revVal2: url,
        revVal3: editing?.revVal3 || '',
        revVal4: editing?.revVal4 || '',
        revVal5: editing?.revVal5 || '',
        validateDt: editing?.validateDt || null,
        step: form.step,
        useYn: form.useYn,
      };

      if (editing) {
        await codeApi.editCode(editing.commonUuid || editing.id, payload);
        enqueueSnackbar('수정되었습니다.', { variant: 'success' });
      } else {
        await codeApi.addComCode(payload);
        enqueueSnackbar('추가되었습니다.', { variant: 'success' });
      }
      setDialogOpen(false);
      load();
      // 헤더는 화면이 처음 뜰 때 한 번만 읽는다. 알려 주지 않으면 새로고침 전까지 안 바뀐다.
      window.dispatchEvent(new Event(HEADER_LINKS_CHANGED));
    } catch {
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.revVal1}" 바로가기를 삭제하시겠습니까?`)) return;
    try {
      await codeApi.removeCode(row.commonUuid || row.id);
      enqueueSnackbar('삭제되었습니다.', { variant: 'success' });
      load();
      window.dispatchEvent(new Event(HEADER_LINKS_CHANGED));
    } catch {
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  const columns = [
    { field: 'step', headerName: '순서', width: 70 },
    { field: 'revVal1', headerName: '이름', width: 180 },
    {
      field: 'revVal2', headerName: '주소', flex: 1, minWidth: 240,
      renderCell: ({ value }) => (value ? (
        <Link
          href={value}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {value}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      ) : null),
    },
    {
      field: 'useYn', headerName: '사용', width: 80,
      renderCell: ({ value }) => (
        <Chip
          label={value === 'Y' ? '사용' : '미사용'}
          color={value === 'Y' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: '_actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton size="small" onClick={() => openEdit(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const urlError = form.url.trim() !== '' && !isValidUrl(form.url);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          화면 오른쪽 위 &quot;업무용 사이트&quot; 목록에 나올 바로가기입니다. 순서가 작을수록 위에 나옵니다.
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          바로가기 추가
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableColumnMenu
          density="compact"
          pageSizeOptions={[20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        />
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '바로가기 수정' : '바로가기 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="이름"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              size="small"
              required
              helperText="목록에 보일 이름입니다. 예: 더존, 안랩 파트너포탈"
            />
            <TextField
              label="주소"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              size="small"
              required
              error={urlError}
              helperText={urlError
                ? 'http:// 또는 https:// 로 시작해야 합니다.'
                : '예: https://partner4u.ahnlab.com/'}
            />
            <TextField
              label="순서"
              value={form.step}
              onChange={(e) => setForm((f) => ({ ...f, step: e.target.value }))}
              size="small"
              type="number"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.useYn === 'Y'}
                  onChange={(e) => setForm((f) => ({ ...f, useYn: e.target.checked ? 'Y' : 'N' }))}
                />
              )}
              label="사용 여부"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinkCodeTabPanel;
